import { useEffect, useState, useCallback, type RefObject } from "react";
import type { ScrollDirection } from "./ScrollButton";

export const useScrollableList = (
  scrollRef: RefObject<HTMLDivElement | null>,
  itemCount: number,
) => {
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const items = Array.from(container.children) as HTMLElement[];
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.target === first) setAtStart(entry.isIntersecting);
          if (entry.target === last) setAtEnd(entry.isIntersecting);
        }
      },
      { root: container, threshold: 0.99 },
    );

    observer.observe(first);
    observer.observe(last);
    return () => observer.disconnect();
  }, [scrollRef, itemCount]);

  const isScrollable = !atStart || !atEnd;

  const handleScrollClick = useCallback(
    (direction: ScrollDirection) => {
      const container = scrollRef.current;
      if (!container) return;

      const prefersReducedMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const behavior = prefersReducedMotion ? "auto" : "smooth";

      const items = Array.from(container.children) as HTMLElement[];
      const containerRect = container.getBoundingClientRect();

      if (direction === "right") {
        const target = items.find(
          p => p.getBoundingClientRect().right > containerRect.right,
        );
        target?.scrollIntoView({ behavior, inline: "start", block: "nearest" });
      } else {
        const target = [...items]
          .reverse()
          .find(p => p.getBoundingClientRect().left < containerRect.left);
        target?.scrollIntoView({ behavior, inline: "end", block: "nearest" });
      }
    },
    [scrollRef],
  );

  return { atStart, atEnd, isScrollable, handleScrollClick };
};
